import { OrgInput } from "../../schema/types/org/input";
import { parseLink } from "../../utils/linkParser/linkParser";
import { formSet } from "../gvc/testNewForm/utils";

export function getValidatorDetail(id: string) {
  return async function validator(v: OrgInput): Promise<boolean | null> {
    // setup
    formSet(id, `meta.valid`, false);
    formSet(id, `meta.validating`, true);
    formSet(id, `errors`, {});
    formSet(id, `warnings`, {});

    const e: Partial<Record<string, { message: string }>> = {};
    const w: Partial<Record<string, { message: string }>> = {};

    function setError(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      e[path] = { message: message };
    }

    function setWarning(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      const currentWarning = w[path];
      if (currentWarning) {
        return;
      }
      w[path] = { message: message };
    }

    // setup end

    try {
      if (v.description.length > 250) {
        setError(`description`, `Description can be at most 250 characters`);
      }

      // tags start
      for (const tagIndexStr in v.tags) {
        const tagIndex = Number(tagIndexStr);
        const tag = v.tags[tagIndex];
        if (!tag.name) {
          setError(`tags.${tagIndex}.name`, "Tag key is required");
          setError(`tagsroot`, "Tag keys are required");
        }
      }

      if (v.tags.length > 50) {
        setError(`tagsroot`, "Tags cannot be more than 50 items");
      }
      // tags end

      let xn: number;
      if (v.sessionTimeoutSeconds.length > 0) {
        xn = Number(v.sessionTimeoutSeconds);
        if (xn < 15 * 60) {
          setError(`sessionTimeoutSeconds`, "Session Timeout must be at least 15 * 60 (900) seconds");
        }
      }

      if (v.security.threatDetection.isEnabled) {
        if (!v.security.threatDetection.syslog.host) {
          setError(`security.threatDetection.syslog.host`, "Host is required");
        } else {
          let regex = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.(?!-)[A-Za-z0-9-]{1,63}(?<!-))*$/;
          if (!regex.test(v.security.threatDetection.syslog.host)) {
            setError(`security.threatDetection.syslog.host`, `Host must be a valid match for regex: ${regex}`);
          }
        }

        if (v.security.threatDetection.syslog.port.length < 1) {
          setError(`security.threatDetection.syslog.port`, "Port is required");
        } else {
          xn = Number(v.security.threatDetection.syslog.port);
          if (xn < 1) {
            setError(`security.threatDetection.syslog.port`, `Port must be at least 1`);
          }
          if (xn > 100000) {
            setError(`security.threatDetection.syslog.port`, `Port can be at most 100000`);
          }
        }
      }

      if (v.observability.logsRetentionDays.length > 0) {
        xn = Number(v.observability.logsRetentionDays);
        if (xn < 0) {
          setError(`observability.logsRetentionDays`, `Logs Retention must be at least 0 days`);
        }
        if (xn > 3650) {
          setError(`observability.logsRetentionDays`, `Logs Retention can be at most 3650 days`);
        }
      }
      if (v.observability.metricsRetentionDays.length > 0) {
        xn = Number(v.observability.metricsRetentionDays);
        if (xn < 0) {
          setError(`observability.metricsRetentionDays`, `Metrics Retention must be at least 0 days`);
        }
        if (xn > 3650) {
          setError(`observability.metricsRetentionDays`, `Metrics Retention can be at most 3650 days`);
        }
      }
      if (v.observability.tracesRetentionDays.length > 0) {
        xn = Number(v.observability.tracesRetentionDays);
        if (xn < 0) {
          setError(`observability.tracesRetentionDays`, `Traces Retention must be at least 0 days`);
        }
        if (xn > 3650) {
          setError(`observability.tracesRetentionDays`, `Traces Retention can be at most 3650 days`);
        }
      }
      for (const indexStr in v.observability.defaultAlertEmails) {
        const index = Number(indexStr);
        const email = v.observability.defaultAlertEmails[index];
        if (!email.value) {
          setError(`observability.defaultAlertEmails.${index}.value`, "Email is required");
          setError(`observability.defaultAlertEmailsroot`, "Email values can be at most 63 characters");
        }
        if (!email.value.includes(`@`)) {
          setError(`observability.defaultAlertEmails.${index}.value`, "Email is not a valid address");
          setError(`observability.defaultAlertEmailsroot`, "Email values need to be valid addresses");
        }
      }

      for (const indexStr in v.logging) {
        const index = Number(indexStr);
        const log = v.logging[index];

        if (!log.provider) {
          setError(`logging.${index}.provider`, "Provider selection is required");
          continue;
        }

        if (log.provider === "cloudWatch") {
          const l = log.cloudWatch;
          if (!l.credentials?.link) {
            setError(`logging.${index}.cloudWatch.credentials`, "Credentials secret is required");
          } else {
            const parsedLink = parseLink(l.credentials.link, { useInputCtx: true });
            if (!parsedLink.isValid) {
              setError(`logging.${index}.cloudWatch.credentials`, parsedLink.errorMessage);
            }
          }

          if (!l.retentionDays) {
            setError(`logging.${index}.cloudWatch.retentionDays`, "Retention Days is required");
          }
          if (!l.region) {
            setError(`logging.${index}.cloudWatch.region`, "Region is required");
          }
          if (!l.groupName) {
            setError(`logging.${index}.cloudWatch.groupName`, "Group Name is required");
          }
          if (!l.streamName) {
            setError(`logging.${index}.cloudWatch.streamName`, "Stream Name is required");
          }

          for (const fieldIndexStr in l.extractFields) {
            const fieldIndex = Number(fieldIndexStr);
            const field = l.extractFields[fieldIndex];
            if (!field.name) {
              setError(`logging.${fieldIndex}.cloudWatch.extractFields.${fieldIndex}.name`, "Key is required");
              setError(`logging.${fieldIndex}.cloudWatch.extractFieldsroot`, "Keys are required");
            }
            if (!field.value) {
              setError(`logging.${fieldIndex}.cloudWatch.extractFields.${fieldIndex}.value`, "Value is required");
              setError(`logging.${fieldIndex}.cloudWatch.extractFieldsroot`, "Values are required");
            }
          }
        }

        if (log.provider === "coralogix") {
          const l = log.coralogix;
          if (!l.credentials?.link) {
            setError(`logging.${index}.coralogix.credentials`, "Credentials secret is required");
          } else {
            const parsedLink = parseLink(l.credentials.link, { useInputCtx: true });
            if (!parsedLink.isValid) {
              setError(`logging.${index}.coralogix.credentials`, parsedLink.errorMessage);
            }
          }

          if (!l.cluster) {
            setError(`logging.${index}.coralogix.cluster`, "Cluster is required");
          }
        }

        if (log.provider === "datadog") {
          const l = log.datadog;
          if (!l.credentials?.link) {
            setError(`logging.${index}.datadog.credentials`, "Credentials secret is required");
          } else {
            const parsedLink = parseLink(l.credentials.link, { useInputCtx: true });
            if (!parsedLink.isValid) {
              setError(`logging.${index}.datadog.credentials`, parsedLink.errorMessage);
            }
          }

          if (!l.host) {
            setError(`logging.${index}.datadog.host`, "Host is required");
          }
        }

        if (log.provider === "elastic") {
          if (!log.elastic.provider) {
            setError(`logging.${index}.elastic.provider`, "Provider selection is required");
          }

          if (log.elastic.provider === "aws") {
            const l = log.elastic.aws;

            if (!l.credentials?.link) {
              setError(`logging.${index}.elastic.aws.credentials`, "Credentials secret is required");
            } else {
              const parsedLink = parseLink(l.credentials.link, { useInputCtx: true });
              if (!parsedLink.isValid) {
                setError(`logging.${index}.elastic.aws.credentials`, parsedLink.errorMessage);
              }
            }

            if (!l.host) {
              setError(`logging.${index}.elastic.aws.host`, "Host is required");
            }
            if (!l.region) {
              setError(`logging.${index}.elastic.aws.region`, "Region is required");
            }
            if (l.port.length < 1) {
              setError(`logging.${index}.elastic.aws.port`, "Port is required");
            }
            if (!l.index) {
              setError(`logging.${index}.elastic.aws.index`, "Index is required");
            }
            if (!l.type) {
              setError(`logging.${index}.elastic.aws.type`, "Type is required");
            }
          }
          if (log.elastic.provider === "elasticCloud") {
            const l = log.elastic.elasticCloud;

            if (!l.credentials?.link) {
              setError(`logging.${index}.elastic.elasticCloud.credentials`, "Credentials secret is required");
            } else {
              const parsedLink = parseLink(l.credentials.link, { useInputCtx: true });
              if (!parsedLink.isValid) {
                setError(`logging.${index}.elastic.elasticCloud.credentials`, parsedLink.errorMessage);
              }
            }

            if (!l.cloudId) {
              setError(`logging.${index}.elastic.elasticCloud.cloudId`, "Cloud ID is required");
            }
            if (!l.index) {
              setError(`logging.${index}.elastic.elasticCloud.index`, "Index is required");
            }
            if (!l.type) {
              setError(`logging.${index}.elastic.elasticCloud.type`, "Type is required");
            }
          }
          if (log.elastic.provider === "generic") {
            const l = log.elastic.generic;

            if (!l.credentials?.link) {
              setError(`logging.${index}.elastic.generic.credentials`, "Credentials secret is required");
            } else {
              const parsedLink = parseLink(l.credentials.link, { useInputCtx: true });
              if (!parsedLink.isValid) {
                setError(`logging.${index}.elastic.generic.credentials`, parsedLink.errorMessage);
              }
            }

            if (!l.host) {
              setError(`logging.${index}.elastic.generic.host`, "Host is required");
            }
            if (!l.index) {
              setError(`logging.${index}.elastic.generic.index`, "Index is required");
            }
            if (!l.type) {
              setError(`logging.${index}.elastic.generic.type`, "Type is required");
            }
            if (l.path.length > 0) {
              const regex = /^\//;
              if (!regex.test(l.path)) {
                setError(`logging.${index}.elastic.generic.path`, `Path must be a valid match for regex: ${regex}`);
              }
            }
          }
        }

        if (log.provider === "fluentd") {
          const l = log.fluentd;
          if (!l.host) {
            setError(`logging.${index}.fluentd.host`, "Host is required");
          }
          if (l.port.length > 1) {
            xn = Number(l.port);
            if (xn < 1) {
              setError(`logging.${index}.fluentd.port`, "Port must be at least 1");
            }
            if (xn > 65535) {
              setError(`logging.${index}.fluentd.port`, "Port can be at most 65535");
            }
          }
        }

        if (log.provider === "logzio") {
          const l = log.logzio;
          if (!l.credentials?.link) {
            setError(`logging.${index}.logzio.credentials`, "Credentials secret is required");
          } else {
            const parsedLink = parseLink(l.credentials.link, { useInputCtx: true });
            if (!parsedLink.isValid) {
              setError(`logging.${index}.logzio.credentials`, parsedLink.errorMessage);
            }
          }

          if (!l.listenerHost) {
            setError(`logging.${index}.logzio.listenerHost`, "Listener Host is required");
          }
        }

        if (log.provider === "s3") {
          const l = log.s3;
          if (!l.credentials?.link) {
            setError(`logging.${index}.s3.credentials`, "Credentials secret is required");
          } else {
            const parsedLink = parseLink(l.credentials.link, { useInputCtx: true });
            if (!parsedLink.isValid) {
              setError(`logging.${index}.s3.credentials`, parsedLink.errorMessage);
            }
          }

          if (!l.bucket) {
            setError(`logging.${index}.s3.bucket`, "Bucket is required");
          }
          if (!l.region) {
            setError(`logging.${index}.s3.region`, "Region is required");
          }
        }

        if (log.provider === "stackdriver") {
          const l = log.stackdriver;
          if (!l.credentials?.link) {
            setError(`logging.${index}.stackdriver.credentials`, "Credentials secret is required");
          } else {
            const parsedLink = parseLink(l.credentials.link, { useInputCtx: true });
            if (!parsedLink.isValid) {
              setError(`logging.${index}.stackdriver.credentials`, parsedLink.errorMessage);
            }
          }

          if (!l.location) {
            setError(`logging.${index}.stackdriver.location`, "Location is required");
          }
        }

        if (log.provider === "syslog") {
          const l = log.syslog;
          if (!l.host) {
            setError(`logging.${index}.syslog.host`, "Host is required");
          }
          if (l.port.length < 1) {
            setError(`logging.${index}.syslog.port`, "Port is required");
          }
          if (!l.mode) {
            setError(`logging.${index}.syslog.mode`, "Mode is required");
          }
          if (!l.format) {
            setError(`logging.${index}.syslog.format`, "Format is required");
          }
          if (!l.severity) {
            setError(`logging.${index}.syslog.severity`, "Severity is required");
          }
        }
      }

      // setup
      for (const path of Object.keys(e)) {
        const value = e[path];
        formSet(id, `errors.${path}`, value);
      }
      for (const path of Object.keys(w)) {
        const value = w[path];
        formSet(id, `warnings.${path}`, value);
      }

      const hasErrors = Object.keys(e).length > 0;

      formSet(id, `meta.valid`, !hasErrors);
      formSet(id, `meta.validating`, false);

      return hasErrors;
    } catch (e) {
      formSet(id, `errors`, { root: e.message });
      return true;
    }
  };
}
